import { InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { FluentProvider, teamsDarkTheme, teamsLightTheme } from "@fluentui/react-components";
import { StrictMode, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { apiService } from './api';
import { config as defaultConfig, setApiUrl, setEnvData, setUserInfoGlobal } from './api/config';
import App from './App';
import SignedOut from './components/SignedOut';
import { SIGNED_OUT_STORAGE_KEY } from './auth/authActions';
import { buildLoginRequest, buildMsalConfig } from './auth/msalConfig';
import { setTokenProvider } from './auth/tokenProvider';
import './index.css';
import { UserInfo } from './models';
import reportWebVitals from './reportWebVitals';
import { ThemeProvider, useTheme } from './ThemeContext';

const log = (...args: any[]) => console.log('[MSAL init]', ...args);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

const ThemedShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isDarkMode } = useTheme();
    return (
        <FluentProvider theme={isDarkMode ? teamsDarkTheme : teamsLightTheme} style={{ height: "100vh" }}>
            {children}
        </FluentProvider>
    );
};

const AppWrapper = () => {
    const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
    const [initError, setInitError] = useState<string | null>(null);
    const [isSignedOut, setIsSignedOut] = useState(false);
    // Gates the app on a completed sign-in; msalInstance alone is set before auth finishes.
    const [isReady, setIsReady] = useState(false);
    const initCalledRef = useRef(false);

    useEffect(() => {
        log('useEffect fired, initCalledRef =', initCalledRef.current);
        if (initCalledRef.current) {
            log('already called — skipping (StrictMode double-invoke)');
            return;
        }
        initCalledRef.current = true;

        const init = async () => {
            try {
                let backendConfig: Record<string, any> = { ...defaultConfig };
                const response = await fetch('/config');
                if (response.ok) {
                    const fetched = await response.json();
                    backendConfig = { ...backendConfig, ...fetched };
                }

                window.appConfig = backendConfig;
                setEnvData(backendConfig);
                setApiUrl(backendConfig.API_URL);

                const instance = new PublicClientApplication(
                    buildMsalConfig(backendConfig.CLIENT_ID, backendConfig.TENANT_ID)
                );
                await instance.initialize();
                setMsalInstance(instance);

                const redirectResult = await instance.handleRedirectPromise();
                if (redirectResult?.account) {
                    instance.setActiveAccount(redirectResult.account);
                }

                const accounts = instance.getAllAccounts();

                if (accounts.length === 0) {
                    if (sessionStorage.getItem(SIGNED_OUT_STORAGE_KEY) === '1') {
                        setIsSignedOut(true);
                        return;
                    }

                    const staleKeys = Object.keys(sessionStorage).filter(k => k.includes('interaction.status'));
                    if (staleKeys.length > 0) {
                        staleKeys.forEach(k => sessionStorage.removeItem(k));
                    }

                    try {
                        await instance.loginRedirect(buildLoginRequest(backendConfig.CLIENT_ID));
                    } catch (err: any) {
                        if (err?.errorCode !== 'interaction_in_progress') throw err;
                    }
                    return;
                }

                const account = instance.getActiveAccount() ?? accounts[0];
                instance.setActiveAccount(account);
                const loginRequest = buildLoginRequest(backendConfig.CLIENT_ID);

                setTokenProvider(async (forceRefresh?: boolean) => {
                    try {
                        const result = await instance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: forceRefresh || false });
                        return result.accessToken;
                    } catch (err) {
                        if (err instanceof InteractionRequiredAuthError) {
                            await instance.acquireTokenRedirect({ ...loginRequest, account });
                        }
                        return null;
                    }
                });

                let tokenResponse;
                try {
                    tokenResponse = await instance.acquireTokenSilent({ ...loginRequest, account });
                } catch (err) {
                    if (err instanceof InteractionRequiredAuthError) {
                        await instance.acquireTokenRedirect({ ...loginRequest, account });
                        return;
                    }
                    throw err;
                }

                const userInfo: UserInfo = {
                    access_token: tokenResponse.accessToken,
                    expires_on: tokenResponse.expiresOn?.toISOString() ?? "",
                    id_token: tokenResponse.idToken,
                    provider_name: "aad",
                    user_claims: [],
                    user_email: account.username,
                    user_first_last_name: account.name ?? "",
                    user_id: account.localAccountId,
                };
                window.userInfo = userInfo;
                setUserInfoGlobal(userInfo);
                sessionStorage.removeItem(SIGNED_OUT_STORAGE_KEY);
                setIsReady(true);
                log('init complete — rendering app');

                await apiService.sendUserBrowserLanguage();
            } catch (error) {
                console.error('[MSAL init] unhandled error:', error);
                setInitError(error instanceof Error ? error.message : String(error));
            }
        };

        init();
    }, []);

    if (initError) {
        return <div style={{ padding: 24, fontFamily: 'monospace' }}>
            <strong>Authentication error:</strong><br />{initError}
        </div>;
    }

    if (msalInstance && isSignedOut) {
        return (
            <MsalProvider instance={msalInstance}>
                <ThemeProvider>
                    <ThemedShell>
                        <SignedOut clientId={window.appConfig?.CLIENT_ID ?? ''} />
                    </ThemedShell>
                </ThemeProvider>
            </MsalProvider>
        );
    }

    if (!msalInstance || !isReady) return <div>Loading...</div>;

    return (
        <MsalProvider instance={msalInstance}>
            <StrictMode>
                <ThemeProvider>
                    <ThemedShell>
                        <App />
                    </ThemedShell>
                </ThemeProvider>
            </StrictMode>
        </MsalProvider>
    );
};

root.render(<AppWrapper />);
reportWebVitals();
